import React from 'react';

import { Icon } from '@iconify/react';
import { TFunction } from 'i18next';

import { AiEditOptionType } from '~/modules/tools/components/AiEditSelector';
import projectConfig, { PROJECT } from '~/projects/config';
import JDAiLoaderSrc from '~/projects/justdone/assets/ai_loader.riv';
import { ReactComponent as JDAudienceIcon } from '~/projects/justdone/assets/audience.svg';
import JDBubbleImg from '~/projects/justdone/assets/bubble.webp';
import { ReactComponent as JDContinueIcon } from '~/projects/justdone/assets/continue.svg';
import { ReactComponent as JDDetectIcon } from '~/projects/justdone/assets/detect_ai.svg';
import JDEmptyStateImg from '~/projects/justdone/assets/empty_state_default.webp';
import { ReactComponent as JDHumanizeIcon } from '~/projects/justdone/assets/humanize.svg';
import { ReactComponent as JDImproveIcon } from '~/projects/justdone/assets/improve.svg';
import { ReactComponent as JDLongerIcon } from '~/projects/justdone/assets/longer.svg';
import { ReactComponent as JDCheckIcon } from '~/projects/justdone/assets/plagiarism_check.svg';
import { ReactComponent as JDShorterIcon } from '~/projects/justdone/assets/shorter.svg';
import { ReactComponent as JDStarGradientIcon } from '~/projects/justdone/assets/star_gradient.svg';
import { ReactComponent as JDSummarizeIcon } from '~/projects/justdone/assets/summarize.svg';
import { ReactComponent as JDToneIcon } from '~/projects/justdone/assets/tone.svg';
import { ReactComponent as JDTranslateIcon } from '~/projects/justdone/assets/translate.svg';
import SKAiLoaderSrc from '~/projects/sidekicker/assets/ai_loader.riv';
import { ReactComponent as SKAudienceIcon } from '~/projects/sidekicker/assets/audience.svg';
import SKBubbleImg from '~/projects/sidekicker/assets/bubble.webp';
import { ReactComponent as SKContinueIcon } from '~/projects/sidekicker/assets/continue.svg';
import { ReactComponent as SKDetailedReportIcon } from '~/projects/sidekicker/assets/detailed_report.svg';
import { ReactComponent as SKDetectAiIcon } from '~/projects/sidekicker/assets/detect_ai.svg';
import SKEmptyStateImg from '~/projects/sidekicker/assets/empty_state_default.webp';
import { ReactComponent as SKExplainIcon } from '~/projects/sidekicker/assets/explain.svg';
import { ReactComponent as SKFactCheckerIcon } from '~/projects/sidekicker/assets/fact_check.svg';
import { ReactComponent as SKHumanizeIcon } from '~/projects/sidekicker/assets/humanize.svg';
import { ReactComponent as SKAiResearchIcon } from '~/projects/sidekicker/assets/market_researcher.svg';
import { ReactComponent as SKParaphraseIcon } from '~/projects/sidekicker/assets/paraphrase.svg';
import { ReactComponent as SKCheckIcon } from '~/projects/sidekicker/assets/plagiarism_check.svg';
import { ReactComponent as SKRemovePlagiarismIcon } from '~/projects/sidekicker/assets/remove_plagiarism.svg';
import { ReactComponent as SKResetIcon } from '~/projects/sidekicker/assets/reset_icon.svg';
import { ReactComponent as SKStarGradientIcon } from '~/projects/sidekicker/assets/star_gradient.svg';
import { ReactComponent as SKTipsIcon } from '~/projects/sidekicker/assets/tips.svg';

type ToolsConfig = {
  getAiResearcherTitle: (t: TFunction) => string;
  bubbleImg: string;
  emptyStateImg: string;
  getResetButton: (t: TFunction) => JSX.Element;
  getHistoryButtonName: (t: TFunction) => string;
  getAAGInstruction: (t: TFunction) => string;
  getAiEditOptions: (t: TFunction) => AiEditOptionType[];
  clearHistoryIcon: JSX.Element;
  copyIcon: JSX.Element;
  aiEditButtonIcon: JSX.Element;
  aiEditSelectorSubmitIcon: JSX.Element;
  downloadIcon: JSX.Element;
  detailedReportIcon: JSX.Element;
  factCheckIcon: JSX.Element;
  tipsToLowerAiIcon: JSX.Element;
  aiResearchIcon: JSX.Element;
  checkPlagiarismIcon: JSX.Element;
  removePlagiarismIcon: JSX.Element;
  aiDetectIcon: JSX.Element;
  aiHumanizeIcon: JSX.Element;
  paraphraseIcon: JSX.Element;
  summarizeIcon: JSX.Element;
  loaderAnimation: string;
};

const toolsConfigByProject: Readonly<Record<PROJECT, ToolsConfig>> = {
  [PROJECT.JUSTDONE]: {
    getAiResearcherTitle: (t: TFunction): string =>
      t('tools:title.ai_researcher', 'AI Researcher'),
    bubbleImg: JDBubbleImg,
    emptyStateImg: JDEmptyStateImg,
    getResetButton: (t: TFunction): JSX.Element => (
      <>
        <span>{t('general:clear', 'Clear')}</span>
      </>
    ),
    getAAGInstruction: (t: TFunction): string =>
      t(
        'tools:aag.topic_instruction',
        'To create an article, first write the title or topic in this field and Justdone will work its magic!',
      ),
    getAiEditOptions: (t: TFunction): AiEditOptionType[] => [
      {
        text: t('general:improve', 'Improve'),
        value: t('ai_edit_options:values[0]', 'improve this text'),
        originalValue: 'improve',
        icon: <JDImproveIcon />,
      },
      {
        text: t('general:continue_writing', 'Continue Writing'),
        value: t('ai_edit_options:values[1]', 'continue writing this text'),
        originalValue: 'continue',
        icon: <JDContinueIcon />,
      },
      {
        text: t('general:shorter', 'Shorter'),
        value: t('ai_edit_options:values[2]', 'make this text shorter'),
        originalValue: 'shorter',
        icon: <JDShorterIcon />,
      },
      {
        text: t('general:humanize', 'Humanize'),
        value: 'humanize',
        originalValue: 'humanize',
        icon: <JDHumanizeIcon />,
      },
      {
        text: t('general:longer', 'Longer'),
        value: t('ai_edit_options:values[4]', 'make this text longer'),
        originalValue: 'longer',
        icon: <JDLongerIcon />,
      },
      {
        text: t('general:detect_ai', 'Detect Ai'),
        value: 'detect ai',
        originalValue: 'detect ai',
        icon: <JDDetectIcon />,
      },
      {
        text: t('general:summarize', 'Summarize'),
        value: t('ai_edit_options:values[6]', 'summarize this text'),
        originalValue: 'summarize',
        icon: <JDSummarizeIcon />,
      },
      {
        text: t('general:check_plagiarism', 'Check Plagiarism'),
        value: 'check plagiarism',
        originalValue: 'check plagiarism',
        icon: <JDCheckIcon />,
      },
      {
        text: t('general:change_tone', 'Change Tone'),
        value: t(
          'ai_edit_options:values[8]',
          'change tone of voice of this text to [paste new tone]',
        ),
        originalValue: 'change tone',
        icon: <JDToneIcon />,
      },
      {
        text: t('general:explain', 'Explain'),
        value: t('ai_edit_options:values[9]', 'explain this text'),
        originalValue: 'explain',
        icon: <JDImproveIcon />,
      },
      {
        text: t('general:change_audience', 'Change Audience'),
        value: t(
          'ai_edit_options:values[10]',
          'rewrite text for the next audience [paste new audience]',
        ),
        originalValue: 'rewrite',
        icon: <JDAudienceIcon />,
      },
      {
        text: t('general:translate', 'Translate'),
        value: t(
          'ai_edit_options:values[11]',
          'translate this text to: [output language]',
        ),
        originalValue: 'translate',
        icon: <JDTranslateIcon />,
      },
    ],
    getHistoryButtonName: (t: TFunction): string =>
      t('general:show_previous_results', 'Show previous results'),
    clearHistoryIcon: <Icon icon="material-symbols:delete-outline" />,
    aiEditButtonIcon: <JDStarGradientIcon />,
    aiEditSelectorSubmitIcon: <Icon icon="material-symbols:send-outline" />,
    copyIcon: <Icon icon="material-symbols:content-copy-outline" />,
    downloadIcon: <Icon icon="material-symbols:file-download" />,
    detailedReportIcon: <Icon icon="material-symbols:content-paste" />,
    factCheckIcon: <Icon icon="material-symbols:fact-check-outline" />,
    tipsToLowerAiIcon: <Icon icon="material-symbols:lightbulb-2-rounded" />,
    aiResearchIcon: <Icon icon="material-symbols:search" />,
    removePlagiarismIcon: (
      <Icon icon="material-symbols:cleaning-services-outline" />
    ),
    checkPlagiarismIcon: (
      <Icon icon="material-symbols:quick-reference-all-outline" />
    ),
    aiDetectIcon: <Icon icon="material-symbols:smart-toy-outline" />,
    aiHumanizeIcon: <Icon icon="material-symbols:frame-person-outline" />,
    paraphraseIcon: <Icon icon="material-symbols:cached" />,
    summarizeIcon: <Icon icon="ic:round-zoom-in-map" />,
    loaderAnimation: JDAiLoaderSrc,
  },
  [PROJECT.SIDEKICKER]: {
    getAiResearcherTitle: (t: TFunction): string =>
      t('tools:title.market_researcher', 'Market Researcher'),
    bubbleImg: SKBubbleImg,
    emptyStateImg: SKEmptyStateImg,
    getResetButton: (t: TFunction): JSX.Element => (
      <>
        <SKResetIcon />
        <span>{t('general:reset', 'Reset')}</span>
      </>
    ),
    getAAGInstruction: () => '',
    getAiEditOptions: (t: TFunction): AiEditOptionType[] => [
      {
        text: t('general:improve', 'Improve'),
        value: t('ai_edit_options:values[0]', 'improve this text'),
        originalValue: 'improve',
        icon: <Icon icon="carbon:magic-wand" />,
      },
      {
        text: t('general:continue_writing', 'Continue Writing'),
        value: t('ai_edit_options:values[1]', 'continue writing this text'),
        originalValue: 'continue',
        icon: <SKContinueIcon />,
      },
      {
        text: t('general:shorter', 'Shorter'),
        value: t('ai_edit_options:values[2]', 'make this text shorter'),
        originalValue: 'shorter',
        icon: <Icon icon="carbon:text-short-paragraph" />,
      },
      {
        text: t('general:humanize', 'Humanize'),
        value: 'humanize',
        originalValue: 'humanize',
        icon: <SKHumanizeIcon />,
      },
      {
        text: t('general:longer', 'Longer'),
        value: t('ai_edit_options:values[4]', 'make this text longer'),
        originalValue: 'longer',
        icon: <Icon icon="carbon:text-long-paragraph" />,
      },
      {
        text: t('general:detect_ai', 'Detect Ai'),
        value: 'detect ai',
        originalValue: 'detect ai',
        icon: <SKDetectAiIcon />,
      },
      {
        text: t('general:summarize', 'Summarize'),
        value: t('ai_edit_options:values[6]', 'summarize this text'),
        originalValue: 'summarize',
        icon: <Icon icon="carbon:minimize" />,
      },
      {
        text: t('general:check_plagiarism', 'Check Plagiarism'),
        value: 'check plagiarism',
        originalValue: 'check plagiarism',
        icon: <SKCheckIcon />,
      },
      {
        text: t('general:change_tone', 'Change Tone'),
        value: t(
          'ai_edit_options:values[8]',
          'change tone of voice of this text to [paste new tone]',
        ),
        originalValue: 'change tone',
        icon: <Icon icon="carbon:ibm-watson-tone-analyzer" />,
      },
      {
        text: t('general:explain', 'Explain'),
        value: t('ai_edit_options:values[9]', 'explain this text'),
        originalValue: 'explain',
        icon: <SKExplainIcon />,
      },
      {
        text: t('general:change_audience', 'Change Audience'),
        value: t(
          'ai_edit_options:values[10]',
          'rewrite text for the next audience [paste new audience]',
        ),
        originalValue: 'rewrite',
        icon: <SKAudienceIcon />,
      },
      {
        text: t('general:translate', 'Translate'),
        value: t(
          'ai_edit_options:values[11]',
          'translate this text to: [output language]',
        ),
        originalValue: 'translate',
        icon: <Icon icon="carbon:ibm-watson-language-translator" />,
      },
    ],
    getHistoryButtonName: (t: TFunction): string =>
      t('general:history', 'History'),
    clearHistoryIcon: <Icon icon="carbon:trash-can" />,
    aiEditButtonIcon: <SKStarGradientIcon />,
    aiEditSelectorSubmitIcon: <Icon icon="carbon:arrow-up" />,
    copyIcon: <Icon icon="carbon:copy-file" />,
    downloadIcon: <Icon icon="carbon:download" />,
    detailedReportIcon: <SKDetailedReportIcon />,
    factCheckIcon: <SKFactCheckerIcon />,
    tipsToLowerAiIcon: <SKTipsIcon />,
    aiResearchIcon: <SKAiResearchIcon />,
    removePlagiarismIcon: <SKRemovePlagiarismIcon />,
    checkPlagiarismIcon: <SKCheckIcon />,
    aiDetectIcon: <SKDetectAiIcon />,
    aiHumanizeIcon: <SKHumanizeIcon />,
    paraphraseIcon: <SKParaphraseIcon />,
    summarizeIcon: <Icon icon="carbon:minimize" />,
    loaderAnimation: SKAiLoaderSrc,
  },
};

const getConfigByProject = (): ToolsConfig => {
  return toolsConfigByProject[projectConfig.project];
};

export const TOOLS_PROJECT_CONFIG = getConfigByProject();
